import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, Sparkle, Warning, CloudRain } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";

const SEV_STYLE = {
  severe:   "bg-red-50 text-red-700 border-red-200",
  warning:  "bg-amber-50 text-amber-800 border-amber-200",
  advisory: "bg-sky-50 text-sky-700 border-sky-200",
};

export default function Notifications() {
  const { user } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  // Weather feed state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const seenAlertIds = useRef(new Set());
  const bootstrapped = useRef(false);

  const load = () => {
    setLoading(true);
    http.get("/nature/notifications").then((r) => {
      setItems(r.data?.notifications || []);
      setUnread(r.data?.unread || 0);
    }).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  // Weather-obsessed feed — polls every 60s. On the very first load we
  // seed `seenAlertIds` with whatever's already there so we don't toast
  // for pre-existing alerts. Anything that arrives *after* the first poll
  // triggers a push-style sonner toast.
  useEffect(() => {
    let timer;
    const pull = async () => {
      try {
        const { data } = await http.get("/weather/alerts/feed?limit=40");
        const newAlerts = data.alerts || [];
        if (!bootstrapped.current) {
          newAlerts.forEach((a) => seenAlertIds.current.add(a.id));
          bootstrapped.current = true;
        } else {
          newAlerts.forEach((a) => {
            if (!seenAlertIds.current.has(a.id)) {
              seenAlertIds.current.add(a.id);
              if (a.severity === "severe" || a.severity === "warning") {
                toast(a.title, {
                  description: a.excerpt || a.location || "New weather alert",
                  icon: a.severity === "severe" ? "🌪️" : "⚠️",
                  action: {
                    label: "Open",
                    onClick: () => (window.location.href = a.url),
                  },
                });
              }
            }
          });
        }
        setAlerts(newAlerts);
      } catch (e) { /* ignore polling errors */ }
      finally { setAlertsLoading(false); }
    };
    pull();
    timer = setInterval(pull, 60000);
    return () => clearInterval(timer);
  }, []);

  const mark = async (id) => {
    try {
      await http.post(`/nature/notifications/${id}/mark`, { read: true });
      setItems((it) => it.map((x) => x.id === id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    } catch { toast.error("Couldn't mark"); }
  };
  const markAll = async () => {
    try {
      const { data } = await http.post("/nature/notifications/read-all");
      setItems((it) => it.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success(`Marked ${data.marked} as read`);
    } catch { toast.error("Couldn't mark all"); }
  };

  const editorialHero = (
    <section className="mb-10 border-b border-black/10 pb-8" data-testid="notifications-editorial">
      <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        / Alerts &amp; Notifications
      </div>
      <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
        Stay connected to what's happening in nature.
      </h1>
      <div className="mt-6 space-y-4 text-[#3A3830] text-base md:text-lg leading-relaxed max-w-2xl">
        <p>
          Nature is constantly changing—and sometimes those changes need our immediate attention.
          NaturePulse Alerts &amp; Notifications help you stay informed about important
          environmental events, unusual observations, changing conditions, and potential hazards
          in the places you care about.
        </p>
        <p>
          Follow your current location, hometown, favorite places, travel destinations, or
          selected regions and choose the topics that matter to you. NaturePulse can notify you
          about severe weather, flooding, fires, landslides, unusual heat or cold, changing
          water conditions, wildlife activity, seasonal changes, agricultural concerns, and
          other noteworthy observations reported by the community or available from trusted
          sources.
        </p>
        <p>
          You can personalize alerts by <b>location, distance, category, severity, and frequency</b>,
          so you receive useful information without unnecessary notifications.
        </p>
      </div>

      <div className="mt-8 max-w-2xl">
        <div className="font-serif-display text-2xl md:text-3xl tracking-tight text-[#241209]">
          Know what's changing around you.
        </div>
        <p className="mt-3 text-[#3A3830] leading-relaxed">
          From a river beginning to rise to an approaching storm, an unusual wildlife sighting,
          the first seasonal bloom, a wildfire nearby, or a significant environmental event
          elsewhere in the world, NaturePulse helps connect people, places, observations and time.
        </p>
        <p className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          Observe it. Report it. Follow it. Stay informed.
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-amber-300/60 bg-amber-50/70 px-4 py-3 text-xs md:text-sm text-[#3A3830] max-w-2xl">
        <b>Please note:</b> NaturePulse alerts supplement — not replace — official emergency
        warnings. During emergencies, always follow instructions from local authorities and
        official weather and disaster-management agencies.
      </div>
    </section>
  );

  if (!user) {
    return (
      <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32">
        <div className="max-w-[720px] mx-auto px-5 md:px-10">
          {editorialHero}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setTab("inbox")}
              className={
                "inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border " +
                (tab === "inbox" ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white text-[#1B4332] border-black/15")
              }
              data-testid="notif-tab-inbox-anon"
            ><Bell size={12} weight="fill" /> Inbox</button>
            <button
              onClick={() => setTab("weather")}
              className={
                "inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border " +
                (tab === "weather" ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white text-[#1B4332] border-black/15")
              }
              data-testid="notif-tab-weather-anon"
            ><CloudRain size={12} weight="fill" /> Weather alerts {alerts.length > 0 && <span className="rounded-full bg-red-600 text-white px-1.5 text-[9px] ml-1">{alerts.length}</span>}</button>
          </div>
          {tab === "weather" ? (
            <>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Weather-obsessed feed</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">Every storm. Every alert.</h2>
              <p className="mt-2 text-[#3A3830] text-sm max-w-2xl">Merged live feed — no sign-in required.</p>
              <div className="mt-6">
                {alertsLoading ? <div className="font-mono-tech text-[10px] tracking-widest">Loading…</div>
                 : alerts.length === 0 ? (
                   <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
                     <CloudRain size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
                     <div className="font-serif-display text-2xl">All quiet.</div>
                   </div>
                 ) : (
                   <ul className="space-y-3">
                     {alerts.map((a) => (
                       <li key={a.id}>
                         <Link to={a.url || "#"} className="block rounded-xl border border-black/10 bg-white p-4 hover:shadow-md">
                           <div className="flex items-baseline justify-between gap-2 flex-wrap">
                             <span className={"inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase " + (SEV_STYLE[a.severity] || SEV_STYLE.advisory)}>
                               <Warning size={10} weight="fill" /> {a.severity}
                             </span>
                             <span className="font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">{(a.updated_at || a.published_at || "").slice(0, 16).replace("T", " ")}</span>
                           </div>
                           <div className="mt-1 font-serif-display text-lg leading-tight">{a.title}</div>
                           {a.excerpt && <div className="mt-1 text-sm text-[#3A3830] line-clamp-2">{a.excerpt}</div>}
                         </Link>
                       </li>
                     ))}
                   </ul>
                 )}
              </div>
            </>
          ) : (
            <>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Your inbox</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">Sign in to see your alerts.</h2>
              <p className="mt-3 text-[#3A3830]">Follow a species, a location, a category or an individual observation and we'll ping you when something new appears.</p>
              <div className="mt-4"><LoginButton /></div>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="notifications-page">
      <div className="max-w-[720px] mx-auto px-5 md:px-10">
        {editorialHero}

        <div className="flex flex-wrap gap-2 mb-6" data-testid="notif-tabs">
          <button
            data-testid="notif-tab-inbox"
            onClick={() => setTab("inbox")}
            className={
              "inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border " +
              (tab === "inbox"
                ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                : "bg-white text-[#1B4332] border-black/15 hover:bg-black/5")
            }
          >
            <Bell size={12} weight="fill" /> Inbox {unread > 0 && <span className="rounded-full bg-red-600 text-white px-1.5 text-[9px] ml-1">{unread}</span>}
          </button>
          <button
            data-testid="notif-tab-weather"
            onClick={() => setTab("weather")}
            className={
              "inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border " +
              (tab === "weather"
                ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                : "bg-white text-[#1B4332] border-black/15 hover:bg-black/5")
            }
          >
            <CloudRain size={12} weight="fill" /> Weather alerts {alerts.length > 0 && <span className="rounded-full bg-red-600 text-white px-1.5 text-[9px] ml-1">{alerts.length}</span>}
          </button>
        </div>

        {tab === "weather" ? (
          <div data-testid="notif-weather-panel">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Weather-obsessed feed</div>
            <h2 className="font-serif-display text-3xl md:text-4xl">Every storm. Every alert.</h2>
            <p className="mt-2 text-[#3A3830] text-sm max-w-2xl">
              Merged live feed of every article and community observation tagged as a
              weather alert. New severe entries push a toast the moment they land.
            </p>
            <div className="mt-6">
              {alertsLoading ? (
                <div className="font-mono-tech text-[10px] tracking-widest">Loading…</div>
              ) : alerts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
                  <CloudRain size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
                  <div className="font-serif-display text-2xl">All quiet.</div>
                  <div className="mt-1 text-sm text-[#3A3830]">No active weather alerts across the network.</div>
                </div>
              ) : (
                <ul className="space-y-3">
                  {alerts.map((a) => (
                    <li key={a.id} data-testid={`weather-alert-${a.id}`}>
                      <Link
                        to={a.url || "#"}
                        className="block rounded-xl border border-black/10 bg-white p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                          <span className={"inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase " + (SEV_STYLE[a.severity] || SEV_STYLE.advisory)}>
                            <Warning size={10} weight="fill" /> {a.severity}
                          </span>
                          <span className="font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                            {(a.updated_at || a.published_at || "").slice(0, 16).replace("T", " ")}
                          </span>
                        </div>
                        <div className="mt-1 font-serif-display text-lg leading-tight">{a.title}</div>
                        {a.excerpt && <div className="mt-1 text-sm text-[#3A3830] line-clamp-2">{a.excerpt}</div>}
                        <div className="mt-1 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
                          {a.kind} {a.location ? `· ${a.location}` : ""} {a.category ? `· ${a.category}` : ""}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Your inbox</div>
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="font-serif-display text-3xl md:text-4xl">Notifications</h2>
              {unread > 0 && (
                <button
                  onClick={markAll}
                  data-testid="notif-mark-all"
                  className="inline-flex items-center gap-1 rounded-full border border-black/20 px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-white"
                >
                  <Check size={12} weight="bold" /> Mark all read
                </button>
              )}
            </div>
            <div className="mt-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
              {unread} unread · {items.length} total
            </div>

            <div className="mt-6">
              {loading ? (
                <div className="font-mono-tech text-[10px] tracking-widest">Loading…</div>
              ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
                  <Bell size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
                  <div className="font-serif-display text-2xl">All quiet.</div>
                  <div className="mt-1 text-sm text-[#3A3830]">Follow a species or place to start getting updates.</div>
                </div>
              ) : (
                <ul className="space-y-2">
                  {items.map((n) => (
                    <li key={n.id} data-testid={`notif-${n.id}`}>
                      <Link
                        to={n.url || "#"}
                        onClick={() => !n.read && mark(n.id)}
                        className={`block rounded-xl border p-4 transition-colors ${n.read ? "bg-white/60 border-black/10" : "bg-white border-[color:var(--np-brand,#B85F2A)]/40"}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                            {n.kind}
                          </div>
                          <div className="font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                            {(n.created_at || "").slice(0, 16).replace("T", " ")}
                          </div>
                        </div>
                        <div className="mt-1 font-serif-display text-lg leading-tight">{n.title}</div>
                        {n.body && <div className="mt-1 text-sm text-[#3A3830]">{n.body}</div>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
